import React, { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../components/atoms';
import { DataTable } from '../../components/organisms';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { useMusicVideos, useMusicVideoActions } from '../../hooks/useMusicVideos';
import type {
  CreateMusicVideoRequest,
  MusicVideo,
  UpdateMusicVideoRequest,
} from '../../types/api/music-video';
import type { Column, TableAction } from '../../types/table';
import { MusicVideoFormModal } from './components/MusicVideoFormModal';
import { resolveAssetUrl } from '../../utils/url';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const formatDuration = (seconds?: number): string => {
  if (seconds === undefined || seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const MusicVideoManagementPage: React.FC = () => {
  const { fetchMusicVideos } = useMusicVideos();
  const { createMusicVideo, updateMusicVideo, getMusicVideo, deleteMusicVideo } =
    useMusicVideoActions();

  const refreshKeyRef = useRef(0);
  const [selectedMusicVideo, setSelectedMusicVideo] = useState<MusicVideo | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedMusicVideo(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    async (musicVideo: MusicVideo) => {
      try {
        const full = await getMusicVideo(musicVideo.musicVideoId);
        setSelectedMusicVideo(full);
        setIsEditModalOpen(true);
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to load music video details'));
      }
    },
    [getMusicVideo]
  );

  const handleDelete = useCallback((musicVideo: MusicVideo) => {
    setSelectedMusicVideo(musicVideo);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCreateSubmit = useCallback(
    async (
      data: CreateMusicVideoRequest | UpdateMusicVideoRequest,
      thumbnailFile?: File | null,
      videoFile?: File | null
    ) => {
      try {
        await createMusicVideo(
          data as CreateMusicVideoRequest,
          thumbnailFile || undefined,
          videoFile || undefined
        );
        toast.success('Music video created successfully');
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to create music video'));
      }
    },
    [createMusicVideo, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (
      data: CreateMusicVideoRequest | UpdateMusicVideoRequest,
      thumbnailFile?: File | null,
      videoFile?: File | null
    ) => {
      if (!selectedMusicVideo) return;
      try {
        await updateMusicVideo(
          selectedMusicVideo.musicVideoId,
          data as UpdateMusicVideoRequest,
          thumbnailFile || undefined,
          videoFile || undefined
        );
        toast.success('Music video updated successfully');
        setIsEditModalOpen(false);
        setSelectedMusicVideo(null);
        handleRefresh();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to update music video'));
      }
    },
    [selectedMusicVideo, updateMusicVideo, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedMusicVideo) return;
    try {
      await deleteMusicVideo(selectedMusicVideo.musicVideoId);
      toast.success('Music video deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedMusicVideo(null);
      handleRefresh();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to delete music video'));
    }
  }, [selectedMusicVideo, deleteMusicVideo, handleRefresh]);

  const columns = useMemo<Column<MusicVideo>[]>(
    () => [
      {
        id: 'thumbnail',
        header: '',
        accessor: 'thumbnailUrl',
        width: '56px',
        cell: (row) => {
          const url = resolveAssetUrl(row.thumbnailUrl || null);
          return url ? (
            <img src={url} alt="" className="w-10 h-10 rounded object-cover" />
          ) : (
            <div className="w-10 h-10 rounded bg-gray-100" />
          );
        },
      },
      { id: 'title', header: 'Title', accessor: 'title', minWidth: '200px' },
      { id: 'artist', header: 'Artist', accessor: 'artistName' },
      { id: 'album', header: 'Album', accessor: 'albumName', cell: (r) => r.albumName || '—' },
      {
        id: 'duration',
        header: 'Duration',
        accessor: 'durationSeconds',
        cell: (r) => formatDuration(r.durationSeconds),
      },
      { id: 'access', header: 'Access', accessor: 'accessLevel' },
      { id: 'status', header: 'Status', accessor: 'status' },
    ],
    []
  );

  const actions = useMemo<TableAction<MusicVideo>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleDelete]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Music Videos</h1>
          <p className="text-gray-600 mt-1 pl-3">Manage music videos for the SAUNG catalog</p>
        </div>
        <Button
          onClick={handleCreate}
          leftIcon={
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
          }
        >
          Add Music Video
        </Button>
      </div>

      <DataTable<MusicVideo>
        key={refreshKeyRef.current}
        columns={columns}
        fetchData={fetchMusicVideos}
        searchable
        searchPlaceholder="Search music videos..."
        initialPageSize={10}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Music video management table"
      />

      <MusicVideoFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        mode="create"
      />

      <MusicVideoFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedMusicVideo(null);
        }}
        onSubmit={handleEditSubmit}
        mode="edit"
        musicVideo={selectedMusicVideo || undefined}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedMusicVideo(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Music Video"
        message={
          selectedMusicVideo
            ? `Are you sure you want to delete "${selectedMusicVideo.title}"? This action cannot be undone.`
            : 'Are you sure you want to delete this music video?'
        }
      />
    </div>
  );
};

export default MusicVideoManagementPage;
